import * as Dialog from '@radix-ui/react-dialog'
import React from 'react'
import { useAppContext } from '../../providers/context'
import { PostableMessage } from '../home/compose/send'
import { Divider } from '../../components/simple/divider'
import { IconAudio, IconEmail, IconPhone, IconText, IconWhatsapp } from '../../icons/library'
import { Logo } from '../../components/simple/logo'
import { Badge, typeToBadgeStyle } from '../../components/simple/badge'
import { LanguageGlobe, LanguageGlobeIcon } from '../home/messageTags'
import { Tooltip } from '../../components/simple/tooltip'
import { ModalTitle } from '../../layouts/components/modal'
import { showAxiosErrorAsToast, useRequestsWhileMounted, wasCancelled } from '../../request'
import { Loader } from '../../components/simple/loader'
import { AudioPlayer } from '../../components/simple/audio'
import { DelayedTextInput } from '../../components/form/textInput'
import { PostableReply } from '../home/conversation/conversationReply'
import { BasicUser } from '../../components/userSelector/users'

type EmailPreview = {
  external_contact: string
  subject: string
  body: string
  firstMessageDisclaimer: string
  ccNote?: string
  advNote?: string
  ccPreNote?: string
  ccPostNote?: string
  postMessage?: string
  confirmReceipt: boolean
  confirmReceiptNotGoogle: boolean
  context: {
    last_from_you_or_sender_after: number
    last_from_you_or_sender: {
      sender: string
      content: string
    }
    original_after: number
    original: {
      sender: string
      content: string
    }
  }
  images: { [url: string]: string }
  attachments: { [url: string]: string }
  translation: string
  code: string | 0
  message_user_id: number
  messageTo: string
  recipientType: BasicUser['type']
  APP_URL: string
  APP_NAME: string
  replyLine: string
  fromLine: string
  language?: string
  unsubscribeLink: string
  originalMessageInEnglishTranslated: string
  unsubscribeTranslated: string
  audio_only_link?: string
  audio_only_text?: string
  massMessageStatement?: string
  districtLogo?: string
}
type TextPreview = {
  body: string
  allMedia: string[]
  whatsapp: 0 | 1
  external_contact: string
}
type PhonePreview = {
  audio_src: string
  external_contact: string
}

type PreviewRecipient = {
  name: string
  id: number
  type: BasicUser['type']
  descriptive_type: string
  language_globe: string
  languageGlobeJson: LanguageGlobe
  languageFull: string
  email: boolean
  text: boolean | 'whatsapp'
  phone: boolean
  selected?: boolean
  student_pre_label?: string
}

export type MessagePreviewRequest = {
  onClose: () => void
  request: (
    | { sentMessageID: number }
    | { existingMessageID: number }
    | { latest: boolean }
    | PostableMessage
    | PostableReply
  ) & {
    previewSpecificUserID?: number
    search?: string
  }
}

type MessagePreview = EmailPreview | TextPreview | PhonePreview

export const usePreview = () => {
  const { setState } = useAppContext()
  const showExistingMessagePreview = React.useCallback(
    (id: number) => {
      return new Promise<void>((resolve) => {
        setState((s) => ({ ...s, previewRequest: { request: { existingMessageID: id }, onClose: resolve } }))
      })
    },
    [setState]
  )
  const showSentMessagePreview = React.useCallback(
    (id: number) => {
      return new Promise<void>((resolve) => {
        setState((s) => ({ ...s, previewRequest: { request: { sentMessageID: id }, onClose: resolve } }))
      })
    },
    [setState]
  )
  const showLatestMessagePreview = React.useCallback(() => {
    return new Promise<void>((resolve) => {
      setState((s) => ({ ...s, previewRequest: { request: { latest: true }, onClose: resolve } }))
    })
  }, [setState])
  const showMessagePreview = React.useCallback(
    (message: PostableMessage | PostableReply) => {
      return new Promise<void>((resolve) => {
        setState((s) => ({ ...s, previewRequest: { request: message, onClose: resolve } }))
      })
    },
    [setState]
  )
  return { showMessagePreview, showExistingMessagePreview, showSentMessagePreview, showLatestMessagePreview }
}

export const MessagePreviewOverlay: React.FC<{ previewRequest: MessagePreviewRequest }> = ({ previewRequest }) => {
  const { setState } = useAppContext()
  const [previews, setPreviews] = React.useState<MessagePreview[]>([])
  const [recipients, setRecipients] = React.useState<{ people: PreviewRecipient[] } | undefined>(undefined)
  const [selectedRecipient, setSelectedRecipient] = React.useState<PreviewRecipient | undefined>(undefined)
  const [more, setMore] = React.useState(false)
  const [previewSearch, setPreviewSearch] = React.useState('')
  const [loading, setLoading] = React.useState(false)

  const makeRequest = useRequestsWhileMounted()

  const fetchPreview = (data: MessagePreviewRequest['request']) => {
    setLoading(true)
    makeRequest<{ previews: MessagePreview[]; recipients?: { people: PreviewRecipient[] }; more?: boolean }>(
      '/frontend_api/getPreview',
      data
    )
      .then((r) => {
        setPreviews(r.previews)
        if (r.recipients) {
          setRecipients(r.recipients)
          setSelectedRecipient(r.recipients.people.find((r) => r.selected))
        }
        if (r.more !== undefined) setMore(r.more)
        setLoading(false)
      })
      .catch((err) => {
        showAxiosErrorAsToast(err)
        if (!wasCancelled(err)) close()
      })
  }

  React.useEffect(() => {
    fetchPreview({ ...previewRequest.request, search: previewSearch })
  }, [previewRequest, previewSearch])

  const switchSelectedRecipient = (recipient: PreviewRecipient) => {
    setSelectedRecipient(recipient)
    fetchPreview({ ...previewRequest.request, search: previewSearch, previewSpecificUserID: recipient.id })
  }

  const initialLoading = previews.length === 0 && recipients === undefined

  const close = () => {
    setState((s) => {
      s.previewRequest?.onClose()
      return { ...s, previewRequest: undefined }
    })
  }

  return (
    <Dialog.Root
      open={true}
      onOpenChange={(o) => {
        if (!o) close()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={`fixed inset-0 z-[5000] flex cursor-pointer overflow-auto bg-slate-500/25 outline-none data-[state=closed]:animate-modalOverlayHide data-[state=open]:animate-modalOverlayShow`}
        >
          <Dialog.Content
            className={`!pointer-events-none flex min-w-[100vw] max-w-[100vw] gap-4 p-4 outline-none -sm:p-8`}
            onFocusOutside={(e) => {
              e.preventDefault()
            }}
          >
            {initialLoading && <Loader color="text-stone-500/30" />}
            <ModalTitle hidden>Message Preview</ModalTitle>
            {recipients !== undefined && (
              <div
                className={`pointer-events-auto flex min-w-0 flex-shrink-0 cursor-default flex-col overflow-auto rounded-lg bg-white p-3 -sm:hidden ${loading ? 'pointer-events-none *:opacity-50' : ''}`}
              >
                <div className="mb-3 flex items-center justify-between gap-4">
                  <div className="text-2xl font-bold">All Recipients</div>
                  <DelayedTextInput
                    clearable
                    delay={500}
                    placeholder="Search"
                    value={previewSearch}
                    onChange={setPreviewSearch}
                    className="flex-grow rounded-full"
                  />
                </div>
                {more && (
                  <div className="-mt-1 mb-2 rounded-sm bg-warning px-2 py-1 text-sm font-bold">
                    Not showing all recipients. Search to see more
                  </div>
                )}
                {recipients.people.map((r, i) => (
                  <PreviewRecipient
                    key={i} // the same recipient can appear twice so we cant use id
                    recipient={r}
                    selected={r === selectedRecipient}
                    onClick={switchSelectedRecipient}
                  />
                ))}
                {recipients.people.length === 0 && (
                  <div className="bg-slate-100 p-2 text-center font-bold opacity-50">No Recipients Found</div>
                )}
              </div>
            )}
            <div
              id="tutorial_previewBody"
              className={`pointer-events-none flex min-w-0 flex-col items-start justify-center gap-4 *:pointer-events-auto ${recipients ? '' : 'm-auto'}`}
            >
              {previews.map((p, index) =>
                'subject' in p ? (
                  <EmailPreviewElement preview={p} key={index} />
                ) : 'body' in p ? (
                  <TextPreviewElement preview={p} key={index} />
                ) : (
                  <PhonePreviewElement preview={p} key={index} />
                )
              )}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const PreviewRecipient: React.FC<{
  recipient: PreviewRecipient
  selected: boolean
  onClick: (p: PreviewRecipient) => void
}> = ({ recipient, selected, onClick }) => {
  const bs = 'hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3),_0_1px_3px_1px_rgba(60,64,67,0.15)] hover:z-10'
  const disabled = !recipient.email && !recipient.text && !recipient.phone
  const tooltip = disabled
    ? 'This user will not receive any external notifications about this message, but they can view it on this site.'
    : undefined
  const elem = (
    <>
      {recipient.student_pre_label && (
        <div className="border-t border-slate-200 bg-slate-100/50 p-2 font-bold opacity-50">
          {recipient.student_pre_label}
        </div>
      )}
      <div
        className={`${bs} flex items-center gap-2 text-nowrap border-t border-slate-200 p-2 ${selected ? 'bg-slate-50' : 'cursor-pointer bg-white'} ${recipient.type === 'guardian' ? 'pl-6' : ''}`}
        onClick={() => onClick(recipient)}
      >
        <div className={selected ? 'font-bold' : ''}>{recipient.name}</div>
        <Badge className="mr-auto" badgeStyle={typeToBadgeStyle(recipient.type)}>
          {recipient.descriptive_type}
        </Badge>
        {recipient.languageGlobeJson && <LanguageGlobeIcon {...recipient.languageGlobeJson} />}
        {recipient.email && <IconEmail className="text-email" />}
        {recipient.phone && <IconPhone className="text-phone" />}
        {recipient.text &&
          (recipient.text === 'whatsapp' ? <IconWhatsapp className="text-text" /> : <IconText className="text-text" />)}
      </div>
    </>
  )
  return tooltip ? <Tooltip text={tooltip}>{elem}</Tooltip> : elem
}

const EmailPreviewElement: React.FC<{ preview: EmailPreview }> = ({ preview }) => {
  function prepareText(text: string) {
    return text.replace('<a', '<a target="_blank"')
  }

  function getContextLabel(val: number) {
    return (
      <div className="opacity-50">
        +{val} message{val > 1 ? 's' : ''}
      </div>
    )
  }

  const bodyWithImages = (
    <>
      <div dangerouslySetInnerHTML={{ __html: prepareText(preview.body) }} />
      {Object.entries(preview.images).map(([url, name]) => (
        <img key={url} src={url} alt={name} height="150px" />
      ))}
    </>
  )
  return (
    <div className="flex max-w-full cursor-default flex-col overflow-hidden rounded-lg bg-white shadow-lg">
      <div className="whitespace-nowrap p-4 font-bold shadow">
        From: {preview.fromLine}&nbsp;&nbsp;&nbsp;&nbsp; To: {preview.messageTo}
      </div>
      <div className="overflow-auto p-4">
        <div className="font-bold">{preview.subject}</div>
        <Divider className="mb-2 mt-1" />
        {preview.districtLogo && <img src={preview.districtLogo} className="mb-[15px] max-h-[50px]" height="50px" />}
        {preview.ccPreNote && (
          <div className="mb-1 bg-warning p-2" dangerouslySetInnerHTML={{ __html: preview.ccPreNote }}></div>
        )}
        {preview.ccNote && (
          <div className="mb-1 bg-warning p-2" dangerouslySetInnerHTML={{ __html: preview.ccNote }}></div>
        )}
        {preview.translation ? (
          <div dangerouslySetInnerHTML={{ __html: prepareText(preview.translation) }} />
        ) : (
          bodyWithImages
        )}

        {preview.audio_only_link && (
          <a href={preview.audio_only_link}>
            <IconAudio /> &nbsp;&zwnj;&nbsp;&zwnj;Listen to this message
          </a>
        )}

        {preview.translation && (
          <>
            <br />
            <br />
            <b>{preview.originalMessageInEnglishTranslated}</b>
            {preview.confirmReceipt && <br />}
            {bodyWithImages}
          </>
        )}

        {preview.attachments && (
          <ul className="mt-2 list-inside list-disc">
            {Object.entries(preview.attachments).map(([url, name]) => (
              <li key={url}>
                <a className="text-link" href={url} target="_blank">
                  {name}
                </a>
              </li>
            ))}
          </ul>
        )}

        {preview.context && (
          <>
            <Divider className="my-2" />
            <div>
              {preview.context.last_from_you_or_sender_after > 0 &&
                getContextLabel(preview.context.last_from_you_or_sender_after)}
              {preview.context.last_from_you_or_sender && (
                <div className="flex flex-col border-l border-gray-500 pl-2 leading-5">
                  <b>{preview.context.last_from_you_or_sender.sender}</b>
                  <div dangerouslySetInnerHTML={{ __html: preview.context.last_from_you_or_sender.content }} />
                </div>
              )}
              {preview.context.original_after > 0 && getContextLabel(preview.context.original_after)}
              {preview.context.original && (
                <div className="flex flex-col border-l border-gray-500 pl-2 leading-5">
                  <b>{preview.context.original.sender}</b>
                  <div dangerouslySetInnerHTML={{ __html: preview.context.original.content }} />
                </div>
              )}
            </div>
          </>
        )}
        <Divider className="my-2" />

        <div className="flex">
          <div className="leading-5">
            {!preview.ccPreNote && preview.ccNote && <div dangerouslySetInnerHTML={{ __html: preview.ccNote }}></div>}
            {preview.postMessage && (
              <div className="opacity-70" dangerouslySetInnerHTML={{ __html: preview.postMessage }}></div>
            )}
            {preview.ccPostNote && <div dangerouslySetInnerHTML={{ __html: prepareText(preview.ccPostNote) }} />}
            {preview.advNote && (
              <div className="opacity-50" dangerouslySetInnerHTML={{ __html: preview.advNote }}></div>
            )}
            {!preview.confirmReceipt && (
              <div
                className="pointer-events-none"
                dangerouslySetInnerHTML={{ __html: prepareText(preview.replyLine) }}
              />
            )}
            {preview.recipientType === 'teacher' && (
              <div>You can disable these messages in user preferences after logging in.</div>
            )}
            {preview.recipientType === 'guardian' && (
              <div className="text-stone-600">
                {preview.unsubscribeTranslated} <span className="text-link">unsubscribe</span>
              </div>
            )}
          </div>
          <Logo className="mb-auto ml-8" size="lg" />
        </div>
      </div>
    </div>
  )
}

function linkify(text: string) {
  let urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi
  return text.replace(urlRegex, function (url) {
    if (
      url.includes('/uLt/') ||
      url.includes('/lL/') ||
      url.includes('/uL/') ||
      url.includes('/uLe/') ||
      url.includes('/contactsu/') ||
      url.includes('/trophyu/') ||
      url.includes('/u/')
    )
      //  || url.includes('/contactsl/') || url.includes('/trophyl/') || url.includes('/l/')
      return url
    return '<a href="' + url + '">' + url + '</a>'
  })
}

const TextPreviewTail = ({ className }: { className: string }) => (
  <div
    className={className}
    style={{
      position: 'absolute',
      bottom: '-4px',
      left: '-12px',
      width: '20px',
      height: '20px',
      clipPath:
        'polygon(100% 0, 100% 39%, 89% 56%, 76% 68%, 59% 78%, 42% 84%, 32% 83%, 26% 76%, 28% 69%, 33% 63%, 45% 47%, 53% 29%, 57% 12%, 59% 0)',
    }}
  />
)

const TextPreviewElement: React.FC<{ preview: TextPreview }> = ({ preview }) => {
  function prepareText(text: string) {
    const linkifiedText = linkify(text.replace(/(?:\r\n|\r|\n)/g, '<br>'))
    return linkifiedText.replace(/<a /g, '<a target="_blank" ')
  }

  return (
    <div
      className={`relative max-h-[45vh] max-w-[400px] cursor-default rounded-lg p-4 shadow-lg [&_a]:text-white ${preview.whatsapp ? 'bg-whatsapp' : 'bg-link'} text-white`}
    >
      <div className="h-full overflow-auto">
        <b className="mb-4">To: {preview.external_contact.replace('+1', '')}</b>
        <div className="max-h-[5px] min-h-[5px]" />
        <div dangerouslySetInnerHTML={{ __html: prepareText(preview.body) }} />
        {preview.allMedia.map((url) =>
          url.includes('audRAW') ? <AudioPlayer key={url} src={url} /> : <img key={url} src={url} height="100px" />
        )}
      </div>
      <TextPreviewTail className={preview.whatsapp ? 'bg-whatsapp' : 'bg-link'} />
    </div>
  )
}

const PhonePreviewElement: React.FC<{ preview: PhonePreview }> = ({ preview }) => {
  return (
    <div className="flex cursor-default flex-col gap-2 rounded-lg bg-white p-4 shadow-lg">
      <b>To: {preview.external_contact.replace('+1', '')}</b>
      <AudioPlayer src={preview.audio_src} reloadWidthFix />
    </div>
  )
}
